import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { History, Eye, Edit, Archive, Trash2 } from 'lucide-react';

export default function HistoriqueTab({
  historique,
  handlePreview,
  handleEdit,
  handleArchive,
  handleDelete,
}) {
  return (
            <div className="space-y-4">
              {historique.length === 0 ? (
                <Card className="border-0 shadow-lg">
                  <CardContent className="py-12 text-center">
                    <History className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                    <h3 className="text-xl font-semibold text-slate-800 mb-2">Aucun historique</h3>
                    <p className="text-slate-600">
                      L&apos;historique des paramètres apparaîtra ici.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                historique.map((params) => (
                  <Card 
                    key={params.id} 
                    className={`border-0 shadow-lg overflow-hidden transition-all hover:shadow-xl ${
                      params.is_archived ? 'opacity-70' : ''
                    }`}
                  >
                    <CardHeader className="bg-slate-50 border-b border-slate-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${
                            params.is_active && !params.is_archived 
                              ? 'bg-green-100' 
                              : params.is_archived 
                                ? 'bg-slate-200' 
                                : 'bg-blue-100'
                          }`}>
                            <History className={`h-5 w-5 ${
                              params.is_active && !params.is_archived 
                                ? 'text-green-600' 
                                : params.is_archived 
                                  ? 'text-slate-500' 
                                  : 'text-blue-600'
                            }`} />
                          </div>
                          <div>
                            <CardTitle className="text-lg flex items-center gap-2">
                              Paramètres du {new Date(params.created_at).toLocaleDateString('fr-FR')}
                              {params.is_active && !params.is_archived && (
                                <Badge className="bg-green-700 text-white font-semibold">Actifs</Badge>
                              )}
                              {params.is_archived && (
                                <Badge className="bg-slate-700 text-white font-semibold">Archivés</Badge>
                              )}
                            </CardTitle>
                            <CardDescription className="font-mono text-xs">
                              ID: {params.id?.substring(0, 12)}...
                            </CardDescription>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            onClick={() => handlePreview(params)} 
                            variant="outline" 
                            size="sm"
                            className="h-8 w-8 p-0"
                            title="Aperçu"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {!params.is_archived && (
                            <>
                              <Button 
                                onClick={() => handleEdit(params)} 
                                variant="outline" 
                                size="sm"
                                className="h-8 w-8 p-0"
                                title="Modifier"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button 
                                onClick={() => handleArchive(params.id)} 
                                variant="outline" 
                                size="sm"
                                className="h-8 w-8 p-0"
                                title="Archiver"
                              >
                                <Archive className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          <Button 
                            onClick={() => handleDelete(params.id)} 
                            variant="outline" 
                            size="sm"
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="font-semibold text-slate-700 mb-2">Coefficients</p>
                          <div className="space-y-1 font-mono text-xs">
                            <div className="flex justify-between">
                              <span className="text-slate-500">GO:</span>
                              <span>{params.coef_gros_oeuvre}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-500">2nd:</span>
                              <span>{params.coef_second_oeuvre}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-500">PIB:</span>
                              <span>{params.coef_intellectuels}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-500">TP:</span>
                              <span>{params.coef_travaux_publics}</span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <p className="font-semibold text-slate-700 mb-2">Remises</p>
                          <div className="space-y-1 font-mono text-xs">
                            <div className="flex justify-between">
                              <span className="text-slate-500">GO:</span>
                              <span className={params.remise_gros_oeuvre > 0 ? 'text-green-600' : ''}>
                                {(params.remise_gros_oeuvre * 100).toFixed(1)}%
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-500">2nd:</span>
                              <span className={params.remise_second_oeuvre > 0 ? 'text-green-600' : ''}>
                                {(params.remise_second_oeuvre * 100).toFixed(1)}%
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-500">PIB:</span>
                              <span className={params.remise_intellectuels > 0 ? 'text-green-600' : ''}>
                                {(params.remise_intellectuels * 100).toFixed(1)}%
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-500">TP:</span>
                              <span className={params.remise_travaux_publics > 0 ? 'text-green-600' : ''}>
                                {(params.remise_travaux_publics * 100).toFixed(1)}%
                              </span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <p className="font-semibold text-slate-700 mb-2">Seuil CA</p>
                          <div className="space-y-1 text-xs">
                            <div className="font-mono text-lg font-bold text-amber-600">
                              {params.seuil_ca.toLocaleString('fr-FR')} €
                            </div>
                            <div className="flex items-center gap-1">
                              {params.seuil_ca_actif ? (
                                <Badge className="bg-orange-700 text-white text-xs font-semibold">Actif</Badge>
                              ) : (
                                <Badge className="bg-slate-700 text-white text-xs font-semibold">Inactif</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div>
                          <p className="font-semibold text-slate-700 mb-2">Dates</p>
                          <div className="space-y-1 text-xs text-slate-600">
                            <div>Créé: {new Date(params.created_at).toLocaleDateString('fr-FR')}</div>
                            <div>MAJ: {new Date(params.updated_at).toLocaleDateString('fr-FR')}</div>
                            {params.archived_at && (
                              <div className="text-slate-400">
                                Archivé: {new Date(params.archived_at).toLocaleDateString('fr-FR')}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
  );
}
